import * as React from 'react';
import { useEffect, useState } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { useNotification, useRequest } from 'shared/hooks';
import { dateString, isBooleanString, isIsoDate } from 'shared/utils';

/**
 * @param {Object} props
 * @param {Array<*>} props.filters
 * @param {Object} props.filterState
 * @param {Function} props.removeFilterChip
 */
export default function FilterChips({
  filters, filterState, removeFilterChip,
}) {
  const { request } = useRequest();
  const [activeChipArr, setActiveChipArr] = useState([]);
  const notification = useNotification();

  function handleDelete(key, value) {
    const subKeys = filters.filter((filter) => filter.entity?.dependentKey === key)
      .map((filter) => filter.param);
    removeFilterChip([key, value], subKeys);
    notification.pushInfo('Filter Removed');
  }

  useEffect(() => {
    (async () => {
      // One chip per active filter. Entity filters hold an id, so they need a request to
      // show a readable label.
      const dirtyChipArr = [];
      const dirtyActiveFilterArr = [];

      Object.entries(filterState)
        .forEach(([key, value]) => {
          const matchedFilter = filters.find((filter) => filter.param === key);
          if (value !== '' && (matchedFilter !== undefined)) {
            if (Array.isArray(value)) {
              value.forEach((val) => {
                dirtyChipArr.push([key, val]);
                dirtyActiveFilterArr.push({ ...matchedFilter, value: val });
              });
            } else {
              dirtyChipArr.push([key, isIsoDate(value) ? dateString(value) : value]);
              if (matchedFilter.entity) {
                matchedFilter.value = value;
                dirtyActiveFilterArr.push(matchedFilter);
              }
            }
          }
        });

      if (dirtyActiveFilterArr.length > 0) {
        let filterKeyNames = [];
        filterKeyNames = await Promise
          .all(dirtyActiveFilterArr.map(
            (matchedFilter) => request(matchedFilter.entity.getRequest(matchedFilter.value)),
          ));

        dirtyActiveFilterArr.forEach(
          (matchedFilter, index) => {
            let newValue;
            if (typeof matchedFilter.entity.getOptionLabel === 'function') {
              newValue = matchedFilter.entity.getOptionLabel(filterKeyNames[index]);
            } else {
              newValue = filterKeyNames[index].name;
            }
            dirtyActiveFilterArr[index].name = newValue;
          },
        );
      }

      const finalChipArr = [];
      dirtyChipArr.forEach(([key, value]) => {
        const matchedFilter = dirtyActiveFilterArr.find(
          (filter) => filter.param === key && filter.value === value,
        );
        if (matchedFilter) {
          finalChipArr.push([matchedFilter.label, key, matchedFilter.name, value]);
        } else {
          finalChipArr.push([key, key, value, value]);
        }
      });
      setActiveChipArr(finalChipArr);
    })();
  }, [filterState, filters, request]);

  return (
    <Stack direction="row" spacing={1} sx={{ marginBottom: '5px', display: 'flex', flexWrap: 'wrap' }}>
      {activeChipArr.map(([name, key, value, val]) => (
        <Chip
          sx={{
            m: '0 5px 5px 0 !important',
            fontSize: '11px !important',
            '&:hover': {
              backgroundColor: 'primary.pale',
              color: 'primary.main',
              borderColor: 'primary.main',
            },
          }}
          label={isBooleanString(value) ? `${name.toUpperCase().replaceAll('_', ' ')}` : `${name.toUpperCase().replaceAll('_', ' ')}: ${value.toUpperCase()}`}
          variant="outlined"
          key={`${key}-${value}`}
          onDelete={() => handleDelete(key, val)}
        />
      ))}
    </Stack>
  );
}
